// ---------------------------------------------------------------------------
// TradeBoxPrimitive — canvas-native trade rectangles (LWC v5 ISeriesPrimitive)
// Renders directly on the chart canvas so boxes move/scale with scroll+zoom.
//
// Box fields:
//   entryTime, exitTime   — snapped candle times
//   entryPrice, slPrice, tpPrice
//   isWin, pnlPct, highlighted
//   isActive              — true when trade entered but not yet exited (replay mode)
// ---------------------------------------------------------------------------
export class TradeBoxPrimitive {
  constructor(boxes, isDark = true) {
    this._boxes = boxes;
    this._isDark = isDark;
    this._series = null;
    this._chart  = null;
  }

  attached({ series, chart }) { this._series = series; this._chart = chart; }
  detached() { this._series = null; this._chart = null; }

  updateAllViews() {}
  priceAxisViews()      { return []; }
  timeAxisViews()       { return []; }
  priceAxisPaneViews()  { return []; }
  timeAxisPaneViews()   { return []; }

  paneViews() {
    const self = this;
    return [{
      renderer() {
        return {
          draw(target) {
            if (!self._series || !self._chart) return;
            const ts = self._chart.timeScale();

            target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr, verticalPixelRatio: vr }) => {
              for (const box of self._boxes) {
                const x1 = ts.timeToCoordinate(box.entryTime);
                const x2 = ts.timeToCoordinate(box.exitTime);
                const yTP    = self._series.priceToCoordinate(box.tpPrice);
                const ySL    = self._series.priceToCoordinate(box.slPrice);
                const yEntry = self._series.priceToCoordinate(box.entryPrice);
                if (x1 == null || x2 == null || yTP == null || ySL == null) continue;

                const bx = Math.min(x1, x2) * hr;
                const bw = Math.abs(x2 - x1) * hr;
                const by = Math.min(yTP, ySL) * vr;
                const bh = Math.abs(ySL - yTP) * vr;

                const green  = '#26a69a';
                const red    = '#ef5350';
                const amber  = '#f59e0b';

                // Active (in-progress) boxes use amber/neutral color
                const col = box.isActive ? amber : (box.isWin ? green : red);

                // Fill
                if (box.isActive) {
                  ctx.fillStyle = 'rgba(245,158,11,0.10)';
                } else {
                  ctx.fillStyle = box.highlighted
                    ? (box.isWin ? 'rgba(38,166,154,0.18)' : 'rgba(239,83,80,0.18)')
                    : (box.isWin ? 'rgba(38,166,154,0.07)' : 'rgba(239,83,80,0.07)');
                }
                ctx.fillRect(bx, by, bw, bh);

                // Border — dashed right edge for active boxes
                ctx.lineWidth = box.highlighted ? 1.5 * hr : 0.8 * hr;
                if (box.isActive) {
                  // Draw 3 solid sides, then dashed right edge
                  ctx.strokeStyle = amber;
                  ctx.setLineDash([]);
                  ctx.beginPath();
                  ctx.moveTo(bx + bw, by); ctx.lineTo(bx, by);        // top
                  ctx.lineTo(bx, by + bh); ctx.lineTo(bx + bw, by + bh); // left + bottom
                  ctx.stroke();
                  ctx.setLineDash([4 * hr, 3 * hr]);
                  ctx.beginPath();
                  ctx.moveTo(bx + bw, by); ctx.lineTo(bx + bw, by + bh); // right dashed
                  ctx.stroke();
                  ctx.setLineDash([]);
                } else {
                  ctx.strokeStyle = col;
                  ctx.setLineDash([]);
                  ctx.strokeRect(bx, by, bw, bh);
                }

                // Entry dashed line
                if (yEntry != null) {
                  const ey = yEntry * vr;
                  ctx.strokeStyle = this._isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)';
                  ctx.lineWidth = 0.8 * hr;
                  ctx.setLineDash([4 * hr, 3 * hr]);
                  ctx.beginPath();
                  ctx.moveTo(bx, ey);
                  ctx.lineTo(bx + bw, ey);
                  ctx.stroke();
                  ctx.setLineDash([]);
                }

                // Labels for ALL boxes
                const fs = Math.round(9 * vr);
                ctx.font = `600 ${fs}px sans-serif`;
                ctx.textBaseline = 'middle';
                const pad = 4 * hr;
                const labelAlpha = box.highlighted ? 0.72 : 0.45;

                // TP label — above the box
                const tpText = `TP  ₹${box.tpPrice.toFixed(1)}  +${((box.tpPrice/box.entryPrice-1)*100).toFixed(1)}%`;
                const tpW = ctx.measureText(tpText).width + pad * 2;
                const tpLabelH = fs + 4 * vr;
                const tpLabelY = by - tpLabelH - 2 * vr;
                ctx.fillStyle = `rgba(0,0,0,${labelAlpha})`;
                ctx.fillRect(bx + bw - tpW - pad, tpLabelY, tpW, tpLabelH);
                ctx.fillStyle = box.isActive ? `rgba(245,158,11,0.8)` : (box.highlighted ? green : 'rgba(38,166,154,0.7)');
                ctx.fillText(tpText, bx + bw - tpW - pad + pad, tpLabelY + tpLabelH / 2);

                // SL label — below the box
                const slText = `SL  ₹${box.slPrice.toFixed(1)}  -${((1-box.slPrice/box.entryPrice)*100).toFixed(1)}%`;
                const slW = ctx.measureText(slText).width + pad * 2;
                const slLabelH = fs + 4 * vr;
                const slLabelY = by + bh + 2 * vr;
                ctx.fillStyle = `rgba(0,0,0,${labelAlpha})`;
                ctx.fillRect(bx + bw - slW - pad, slLabelY, slW, slLabelH);
                ctx.fillStyle = box.isActive ? `rgba(245,158,11,0.8)` : (box.highlighted ? red : 'rgba(239,83,80,0.7)');
                ctx.fillText(slText, bx + bw - slW - pad + pad, slLabelY + slLabelH / 2);

                if (!box.highlighted) continue;

                // Entry label — highlighted (or active) only
                if (yEntry != null) {
                  const entryText = `Entry ₹${box.entryPrice.toFixed(1)}`;
                  const entryW = ctx.measureText(entryText).width + pad * 2;
                  const ey = yEntry * vr;
                  ctx.fillStyle = 'rgba(0,0,0,0.65)';
                  ctx.fillRect(bx + pad, ey - (fs + 4 * vr) / 2 - fs, entryW, fs + 4 * vr);
                  ctx.fillStyle = this._isDark ? 'rgba(255,255,255,0.8)' : 'rgba(17,24,39,0.85)';
                  ctx.fillText(entryText, bx + pad + pad, ey - fs);
                }

                // PnL badge — only for completed trades
                if (!box.isActive) {
                  const pnlText = `${box.pnlPct >= 0 ? '+' : ''}${box.pnlPct.toFixed(2)}%`;
                  ctx.font = `700 ${Math.round(11 * vr)}px sans-serif`;
                  const pnlW = ctx.measureText(pnlText).width + pad * 3;
                  const pnlH = Math.round(11 * vr) + 6 * vr;
                  const pnlX = bx + (bw - pnlW) / 2;
                  const pnlY = by + (bh - pnlH) / 2;
                  ctx.fillStyle = 'rgba(0,0,0,0.72)';
                  ctx.beginPath();
                  ctx.roundRect(pnlX, pnlY, pnlW, pnlH, 3 * hr);
                  ctx.fill();
                  ctx.fillStyle = col;
                  ctx.textBaseline = 'middle';
                  ctx.fillText(pnlText, pnlX + pad * 1.5, pnlY + pnlH / 2);
                } else {
                  // Active trade: show "OPEN" badge in amber
                  const openText = 'OPEN';
                  ctx.font = `700 ${Math.round(11 * vr)}px sans-serif`;
                  const oW = ctx.measureText(openText).width + pad * 3;
                  const oH = Math.round(11 * vr) + 6 * vr;
                  const oX = bx + (bw - oW) / 2;
                  const oY = by + (bh - oH) / 2;
                  ctx.fillStyle = 'rgba(0,0,0,0.72)';
                  ctx.beginPath();
                  ctx.roundRect(oX, oY, oW, oH, 3 * hr);
                  ctx.fill();
                  ctx.fillStyle = amber;
                  ctx.textBaseline = 'middle';
                  ctx.fillText(openText, oX + pad * 1.5, oY + oH / 2);
                }
              }
            });
          }
        };
      }
    }];
  }
}

// Available Studies/Indicators
export const AVAILABLE_STUDIES = [
  // Moving Averages
  { key: 'ema9',    label: 'EMA (9)',    category: 'Moving Averages' },
  { key: 'ema20',   label: 'EMA (20)',   category: 'Moving Averages' },
  { key: 'ema50',   label: 'EMA (50)',   category: 'Moving Averages' },
  { key: 'ema200',  label: 'EMA (200)',  category: 'Moving Averages' },
  { key: 'sma20',   label: 'SMA (20)',   category: 'Moving Averages' },
  { key: 'sma50',   label: 'SMA (50)',   category: 'Moving Averages' },
  { key: 'sma200',  label: 'SMA (200)',  category: 'Moving Averages' },
  { key: 'vwma',    label: 'VWMA (20)',  category: 'Moving Averages' },
  { key: 'hma',     label: 'HMA (20)',   category: 'Moving Averages' },
  // Volatility
  { key: 'bollinger',   label: 'Bollinger Bands',  category: 'Volatility' },
  { key: 'atr',         label: 'ATR (14)',          category: 'Volatility' },
  // Momentum
  { key: 'rsi',         label: 'RSI (14)',          category: 'Momentum' },
  { key: 'macd',        label: 'MACD (12,26,9)',    category: 'Momentum' },
  { key: 'stochastic',  label: 'Stochastic (14,3)', category: 'Momentum' },
  { key: 'cci',         label: 'CCI (20)',           category: 'Momentum' },
  { key: 'williamsr',   label: 'Williams %R (14)',   category: 'Momentum' },
  { key: 'roc',         label: 'ROC (12)',           category: 'Momentum' },
  // Trend
  { key: 'supertrend',  label: 'Supertrend (10,3)', category: 'Trend' },
  { key: 'parabolicsar',label: 'Parabolic SAR',      category: 'Trend' },
  { key: 'adx',         label: 'ADX (14)',           category: 'Trend' },
  { key: 'ichimoku',    label: 'Ichimoku Cloud',     category: 'Trend' },
  // Volume
  { key: 'vwap',        label: 'VWAP',              category: 'Volume' },
  { key: 'obv',         label: 'OBV',               category: 'Volume' },
  { key: 'volumesma',   label: 'Volume SMA (20)',    category: 'Volume' },
];

// Timeframe options
export const TIMEFRAMES = [
  { key: '1m',  label: '1m',  intraday: true },
  { key: '5m',  label: '5m',  intraday: true },
  { key: '15m', label: '15m', intraday: true },
  { key: '1h',  label: '1h',  intraday: true },
  { key: '1d',  label: '1D',  intraday: false },
  { key: '1w',  label: '1W',  intraday: false },
  { key: '1M',  label: '1M',  intraday: false },
];

export const INTRADAY_INTERVALS = ['1m', '5m', '15m', '1h'];

// Chart theme colors
export const getChartColors = (isDark) => ({
  background: isDark ? '#0a0e17' : '#ffffff',
  text: isDark ? '#8b949e' : '#6B7280',
  grid: isDark ? '#1c2333' : '#E5E7EB',
  upColor: '#26a69a',
  downColor: '#ef5350',
  volumeUp: isDark ? 'rgba(38, 166, 154, 0.4)' : 'rgba(38, 166, 154, 0.6)',
  volumeDown: isDark ? 'rgba(239, 83, 80, 0.4)' : 'rgba(239, 83, 80, 0.6)',
  crosshair: isDark ? '#758696' : '#9CA3AF',
  toolbarBg: isDark ? '#0f1419' : '#F9FAFB',
  dropdownBg: isDark ? '#1e222d' : '#FFFFFF',
  indicatorColors: {
    ema9: '#00e5ff', ema20: '#26a69a', ema50: '#42a5f5', ema200: '#ff7043',
    sma20: '#ffca28', sma50: '#ab47bc', sma200: '#ff5722',
    vwma: '#69f0ae', hma: '#f06292',
    bollinger: '#ec407a',
    atr: '#78909c',
    rsi: '#7c4dff',
    macd: '#00bcd4', macdSignal: '#ff9800', macdHist: '#4caf50',
    stochK: '#e91e63', stochD: '#9c27b0',
    cci: '#009688',
    williamsr: '#ff6f00',
    roc: '#8d6e63',
    supertrend: '#76ff03',
    parabolicsar: '#ff1744',
    adx: '#40c4ff',
    ichimoku: '#b2ff59',
    vwap: '#ffd740',
    obv: '#69f0ae',
    volumesma: '#90a4ae',
  }
});

// Indicator calculation functions
export const calculateEMA = (data, period) => {
  const multiplier = 2 / (period + 1);
  const result = new Array(data.length).fill(null);
  if (data.length < period) return result;
  let sum = 0;
  for (let i = 0; i < period; i++) sum += data[i];
  result[period - 1] = sum / period;
  for (let i = period; i < data.length; i++) {
    result[i] = (data[i] - result[i - 1]) * multiplier + result[i - 1];
  }
  return result;
};

export const calculateSMA = (data, period) => {
  const result = new Array(data.length).fill(null);
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = 0; j < period; j++) sum += data[i - j];
    result[i] = sum / period;
  }
  return result;
};

export const calculateWMA = (data, period) => {
  const result = new Array(data.length).fill(null);
  for (let i = period - 1; i < data.length; i++) {
    let num = 0, den = 0;
    for (let j = 0; j < period; j++) { num += data[i - j] * (period - j); den += (period - j); }
    result[i] = num / den;
  }
  return result;
};

export const calculateHMA = (data, period) => {
  const half = Math.floor(period / 2);
  const sq = Math.round(Math.sqrt(period));
  const wmaHalf = calculateWMA(data, half);
  const wmaFull = calculateWMA(data, period);
  const diff = data.map((_, i) => wmaHalf[i] !== null && wmaFull[i] !== null ? 2 * wmaHalf[i] - wmaFull[i] : null);
  return calculateWMA(diff.map(v => v ?? 0), sq).map((v, i) => diff[i] === null ? null : v);
};

export const calculateVWMA = (closes, volumes, period) => {
  const result = new Array(closes.length).fill(null);
  for (let i = period - 1; i < closes.length; i++) {
    let num = 0, den = 0;
    for (let j = 0; j < period; j++) { num += closes[i - j] * volumes[i - j]; den += volumes[i - j]; }
    result[i] = den > 0 ? num / den : null;
  }
  return result;
};

export const calculateRSI = (data, period = 14) => {
  const result = new Array(data.length).fill(null);
  if (data.length < period + 1) return result;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const d = data[i] - data[i - 1];
    if (d > 0) gains += d; else losses -= d;
  }
  let avgGain = gains / period, avgLoss = losses / period;
  result[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < data.length; i++) {
    const d = data[i] - data[i - 1];
    avgGain = (avgGain * (period - 1) + Math.max(d, 0)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(-d, 0)) / period;
    result[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return result;
};

export const calculateMACD = (data) => {
  const ema12 = calculateEMA(data, 12);
  const ema26 = calculateEMA(data, 26);
  const macdLine = data.map((_, i) => ema12[i] !== null && ema26[i] !== null ? ema12[i] - ema26[i] : null);
  const signalInput = macdLine.map(v => v ?? 0);
  const signal = calculateEMA(signalInput, 9);
  const histogram = macdLine.map((v, i) => v !== null && signal[i] !== null ? v - signal[i] : null);
  return { macdLine, signal, histogram };
};

export const calculateStochastic = (candles, kPeriod = 14, dPeriod = 3) => {
  const k = new Array(candles.length).fill(null);
  for (let i = kPeriod - 1; i < candles.length; i++) {
    const slice = candles.slice(i - kPeriod + 1, i + 1);
    const low = Math.min(...slice.map(c => c.low));
    const high = Math.max(...slice.map(c => c.high));
    k[i] = high === low ? 50 : ((candles[i].close - low) / (high - low)) * 100;
  }
  const d = calculateSMA(k.map(v => v ?? 0), dPeriod).map((v, i) => k[i] === null ? null : v);
  return { k, d };
};

export const calculateCCI = (candles, period = 20) => {
  const result = new Array(candles.length).fill(null);
  for (let i = period - 1; i < candles.length; i++) {
    const slice = candles.slice(i - period + 1, i + 1);
    const tps = slice.map(c => (c.high + c.low + c.close) / 3);
    const mean = tps.reduce((a, b) => a + b, 0) / period;
    const md = tps.reduce((a, b) => a + Math.abs(b - mean), 0) / period;
    result[i] = md === 0 ? 0 : (tps[tps.length - 1] - mean) / (0.015 * md);
  }
  return result;
};

export const calculateWilliamsR = (candles, period = 14) => {
  return candles.map((c, i) => {
    if (i < period - 1) return null;
    const slice = candles.slice(i - period + 1, i + 1);
    const high = Math.max(...slice.map(s => s.high));
    const low = Math.min(...slice.map(s => s.low));
    return high === low ? -50 : ((high - c.close) / (high - low)) * -100;
  });
};

export const calculateROC = (data, period = 12) => {
  return data.map((v, i) => i < period || data[i - period] === 0 ? null : ((v - data[i - period]) / data[i - period]) * 100);
};

export const calculateATR = (candles, period = 14) => {
  const tr = candles.map((c, i) => {
    if (i === 0) return c.high - c.low;
    const prev = candles[i - 1].close;
    return Math.max(c.high - c.low, Math.abs(c.high - prev), Math.abs(c.low - prev));
  });
  return calculateEMA(tr, period);
};

export const calculateSupertrend = (candles, period = 10, multiplier = 3) => {
  const atr = calculateATR(candles, period);
  const up = [], dn = [], trend = [];
  candles.forEach((c, i) => {
    const mid = (c.high + c.low) / 2;
    const a = atr[i] || 0;
    up[i] = mid + multiplier * a;
    dn[i] = mid - multiplier * a;
    if (i === 0) { trend[i] = 1; return; }
    const prevClose = candles[i - 1].close;
    dn[i] = dn[i] < (dn[i - 1] ?? dn[i]) || prevClose < (dn[i - 1] ?? dn[i]) ? dn[i] : dn[i - 1] ?? dn[i];
    up[i] = up[i] > (up[i - 1] ?? up[i]) || prevClose > (up[i - 1] ?? up[i]) ? up[i] : up[i - 1] ?? up[i];
    if (trend[i - 1] === -1) trend[i] = c.close > up[i - 1] ? 1 : -1;
    else trend[i] = c.close < dn[i - 1] ? -1 : 1;
  });
  return candles.map((_, i) => ({ value: trend[i] === 1 ? dn[i] : up[i], trend: trend[i] }));
};

export const calculateParabolicSAR = (candles, start = 0.02, increment = 0.02, max = 0.2) => {
  if (candles.length < 2) return new Array(candles.length).fill(null);
  const result = new Array(candles.length).fill(null);
  let bull = true, af = start, ep = candles[0].low, sar = candles[0].high;
  for (let i = 1; i < candles.length; i++) {
    sar = sar + af * (ep - sar);
    if (bull) {
      if (candles[i].low < sar) { bull = false; sar = ep; ep = candles[i].low; af = start; }
      else { if (candles[i].high > ep) { ep = candles[i].high; af = Math.min(af + increment, max); } }
    } else {
      if (candles[i].high > sar) { bull = true; sar = ep; ep = candles[i].high; af = start; }
      else { if (candles[i].low < ep) { ep = candles[i].low; af = Math.min(af + increment, max); } }
    }
    result[i] = sar;
  }
  return result;
};

export const calculateADX = (candles, period = 14) => {
  const result = new Array(candles.length).fill(null);
  if (candles.length < period * 2) return result;
  const tr = [], pdm = [], ndm = [];
  for (let i = 1; i < candles.length; i++) {
    const prev = candles[i - 1];
    tr.push(Math.max(candles[i].high - candles[i].low, Math.abs(candles[i].high - prev.close), Math.abs(candles[i].low - prev.close)));
    pdm.push(Math.max(candles[i].high - prev.high, 0));
    ndm.push(Math.max(prev.low - candles[i].low, 0));
  }
  const smoothed = (arr, p) => {
    let s = arr.slice(0, p).reduce((a, b) => a + b, 0);
    const r = [s];
    for (let i = p; i < arr.length; i++) { s = s - s / p + arr[i]; r.push(s); }
    return r;
  };
  const sTR = smoothed(tr, period), sPDM = smoothed(pdm, period), sNDM = smoothed(ndm, period);
  const dx = sTR.map((t, i) => {
    const pdi = t > 0 ? (sPDM[i] / t) * 100 : 0;
    const ndi = t > 0 ? (sNDM[i] / t) * 100 : 0;
    return pdi + ndi > 0 ? Math.abs(pdi - ndi) / (pdi + ndi) * 100 : 0;
  });
  let adxSum = dx.slice(0, period).reduce((a, b) => a + b, 0);
  result[period * 2] = adxSum / period;
  for (let i = period + 1; i < dx.length; i++) {
    adxSum = (result[period + i - 1] * (period - 1) + dx[i]) / period;
    result[period + i] = adxSum;
  }
  return result;
};

export const calculateVWAP = (candles) => {
  let cumTPV = 0, cumVol = 0;
  return candles.map((c, i) => {
    const tp = (c.high + c.low + c.close) / 3;
    // Reset at start of each day (when time resets to beginning of day)
    const isNewDay = i > 0 && typeof c.time === 'string' && c.time !== candles[i - 1].time;
    if (isNewDay) { cumTPV = 0; cumVol = 0; }
    cumTPV += tp * c.volume;
    cumVol += c.volume;
    return cumVol > 0 ? cumTPV / cumVol : null;
  });
};

export const calculateOBV = (candles) => {
  let obv = 0;
  return candles.map((c, i) => {
    if (i > 0) {
      if (c.close > candles[i - 1].close) obv += c.volume;
      else if (c.close < candles[i - 1].close) obv -= c.volume;
    }
    return obv;
  });
};

export const calculateIchimoku = (candles) => {
  const high = candles.map(c => c.high), low = candles.map(c => c.low);
  const midRange = (arr, i, p) => {
    const s = arr.slice(Math.max(0, i - p + 1), i + 1);
    return (Math.max(...s) + Math.min(...s)) / 2;
  };
  return candles.map((c, i) => ({
    tenkan: i >= 8  ? midRange(high, i, 9)  : null,
    kijun:  i >= 25 ? midRange(high, i, 26) : null,
    senkouA: i >= 25 ? (midRange(high, i, 9) + midRange(high, i, 26)) / 2 : null,
    senkouB: i >= 51 ? midRange(high, i, 52) : null,
  }));
};

export const calculateBollingerBands = (data, period, stdDev) => {
  const middle = calculateSMA(data, period);
  const upper = new Array(data.length).fill(null);
  const lower = new Array(data.length).fill(null);
  for (let i = period - 1; i < data.length; i++) {
    if (middle[i] === null) continue;
    let sum = 0;
    for (let j = 0; j < period; j++) sum += Math.pow(data[i - j] - middle[i], 2);
    const std = Math.sqrt(sum / period);
    upper[i] = middle[i] + stdDev * std;
    lower[i] = middle[i] - stdDev * std;
  }
  return { upper, middle, lower };
};
